// Lightweight wrapper around `hrStatsApi.getPeople` for the People Insights
// drill panel. The drill panel mounts/unmounts as the user opens/closes it;
// react-query keeps the result cached for a minute so re-opening the same
// scope feels instant.

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { hrStatsApi } from '../services/api';
import type {
  HrEmployeeStatus,
  HrFilterMap,
  HrStatsPeopleResponse,
  HrStatsPeopleScope,
} from '../types/hrStats';

export interface UseHrPeopleOptions {
  scope: HrStatsPeopleScope;
  filters: HrFilterMap;
  employeeStatus: HrEmployeeStatus;
  limit?: number;
  offset?: number;
  /** Disable the query (e.g. drill panel is closed). */
  enabled?: boolean;
}

export interface UseHrPeopleResult {
  data: HrStatsPeopleResponse | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
}

const DEFAULT_LIMIT = 100;

export function useHrPeople({
  scope,
  filters,
  employeeStatus,
  limit = DEFAULT_LIMIT,
  offset = 0,
  enabled = true,
}: UseHrPeopleOptions): UseHrPeopleResult {
  const query = useQuery({
    queryKey: ['hr-stats', 'people', scope, filters, employeeStatus, limit, offset],
    queryFn: () =>
      hrStatsApi.getPeople({
        scope,
        filters,
        employee_status: employeeStatus,
        limit,
        offset,
      }),
    enabled,
    staleTime: 60_000,
    // v5 replacement for `keepPreviousData: true` — keeps the prior page's
    // data visible while the next one loads (no flash of empty state).
    placeholderData: keepPreviousData,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
  };
}
